import { PlatformError } from '@djgg/core';
import { canonicalJson } from '@djgg/gameplay-kit';
import {
  validateQualityArtifactRefV2,
  validateQualityClaimV1,
  validateQualityRunStateV2,
  type QualityArtifactRefV2,
  type QualityClaimV1,
  type QualityPhaseV2,
  type QualityRunStateV2,
} from './qualitySchemas.js';

export type QualityTransitionEventV1 =
  | { id: string; type: 'specification-ready' }
  | { id: string; type: 'design-clarification-required'; reason: string }
  | { id: string; type: 'verification-unsupported'; reason: string }
  | { id: string; type: 'core-admitted'; deadline: number }
  | { id: string; type: 'core-started' }
  | { id: string; type: 'core-verified' }
  | { id: string; type: 'expansion-started'; deadline: number }
  | { id: string; type: 'candidate-ready' }
  | { id: string; type: 'media-joined' }
  | { id: string; type: 'final-verified'; manifest: QualityArtifactRefV2 }
  | { id: string; type: 'published' }
  | { id: string; type: 'operation-retry'; deadline?: number }
  | { id: string; type: 'basis-superseded'; deadline: number }
  | { id: string; type: 'failed'; reason: string }
  | { id: string; type: 'cancelled'; reason: string };

export interface QualityTransitionRecordV1 {
  runId: string;
  eventId: string;
  eventDigest: string;
  fromRevision: number;
  toRevision: number;
  fromPhase: QualityPhaseV2;
  toPhase: QualityPhaseV2;
  claim: QualityClaimV1;
  committedAt: number;
}

const TERMINAL = new Set<QualityPhaseV2>([
  'needs-design-clarification',
  'verification-unsupported',
  'published',
  'failed',
  'cancelled',
]);

const NEXT: Readonly<Record<QualityTransitionEventV1['type'], readonly QualityPhaseV2[]>> = {
  'specification-ready': ['contract-pending'],
  'design-clarification-required': ['contract-pending'],
  'verification-unsupported': ['contract-pending'],
  'core-admitted': ['spec-ready'],
  'core-started': ['core-pending'],
  'core-verified': ['core-running'],
  'expansion-started': ['core-verified'],
  'candidate-ready': ['expanding'],
  'media-joined': ['joining-media'],
  'final-verified': ['final-verification'],
  'published': ['publication-ready'],
  'operation-retry': [
    'contract-pending', 'spec-ready', 'core-pending', 'core-running', 'core-verified',
    'expanding', 'joining-media', 'final-verification', 'publication-ready',
  ],
  'basis-superseded': ['core-verified', 'expanding', 'joining-media', 'final-verification'],
  'failed': [
    'contract-pending', 'spec-ready', 'core-pending', 'core-running', 'core-verified',
    'expanding', 'joining-media', 'final-verification', 'publication-ready',
  ],
  'cancelled': [
    'contract-pending', 'spec-ready', 'core-pending', 'core-running', 'core-verified',
    'expanding', 'joining-media', 'final-verification', 'publication-ready',
  ],
};

function eventId(value: string): string {
  if (!/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/.test(value)) {
    throw new PlatformError('validation', 'quality transition requires a stable event id');
  }
  return value;
}

function reason(value: string): string {
  if (!value.trim() || value.length > 256) throw new PlatformError('validation', 'quality transition reason is invalid');
  return value;
}

function target(event: QualityTransitionEventV1, current: QualityRunStateV2): QualityRunStateV2 {
  const next: QualityRunStateV2 = { ...current, revision: current.revision + 1 };
  delete next.terminalReason;
  switch (event.type) {
    case 'specification-ready': return { ...next, phase: 'spec-ready' };
    case 'design-clarification-required': return { ...next, phase: 'needs-design-clarification', terminalReason: reason(event.reason), durableDeadline: null };
    case 'verification-unsupported': return { ...next, phase: 'verification-unsupported', terminalReason: reason(event.reason), durableDeadline: null };
    case 'core-admitted': return { ...next, phase: 'core-pending', durableDeadline: event.deadline };
    case 'core-started': return { ...next, phase: 'core-running' };
    case 'core-verified': return { ...next, phase: 'core-verified', durableDeadline: null };
    case 'expansion-started': return { ...next, phase: 'expanding', durableDeadline: event.deadline };
    case 'candidate-ready': return { ...next, phase: 'joining-media' };
    case 'media-joined': return { ...next, phase: 'final-verification' };
    case 'final-verified': return { ...next, phase: 'publication-ready', finalManifest: validateQualityArtifactRefV2(event.manifest, 'qualityTransition.manifest'), durableDeadline: null };
    case 'published': return { ...next, phase: 'published', durableDeadline: null };
    case 'operation-retry': return { ...next, operationAttempt: current.operationAttempt + 1, durableDeadline: event.deadline ?? current.durableDeadline };
    case 'basis-superseded': return { ...next, phase: 'core-pending', basisEpoch: current.basisEpoch + 1, operationAttempt: 0, durableDeadline: event.deadline };
    case 'failed': return { ...next, phase: 'failed', terminalReason: reason(event.reason), durableDeadline: null };
    case 'cancelled': return { ...next, phase: 'cancelled', terminalReason: reason(event.reason), durableDeadline: null };
  }
}

export function qualityTransitionEventDigest(event: QualityTransitionEventV1): string {
  return canonicalJson(event);
}

export function assertQualityClaim(
  state: QualityRunStateV2,
  claimValue: QualityClaimV1,
  activeClaimValue: QualityClaimV1,
  now: number,
): QualityClaimV1 {
  const claim = validateQualityClaimV1(claimValue);
  const active = validateQualityClaimV1(activeClaimValue);
  if (claim.expectedRunRevision !== state.revision
    || active.expectedRunRevision !== state.revision
    || claim.jobId !== active.jobId
    || claim.claimToken !== active.claimToken
    || claim.leaseGeneration !== active.leaseGeneration
    || claim.leaseExpiry !== active.leaseExpiry
    || claim.leaseExpiry <= now) {
    throw new PlatformError('conflict', 'quality transition claim is stale or expired');
  }
  return claim;
}

export function reduceQualityTransition(
  stateValue: QualityRunStateV2,
  event: QualityTransitionEventV1,
): QualityRunStateV2 {
  const state = validateQualityRunStateV2(stateValue);
  eventId(event.id);
  if (TERMINAL.has(state.phase)) throw new PlatformError('conflict', `terminal quality phase ${state.phase} cannot resume`);
  if (!NEXT[event.type].includes(state.phase)) {
    throw new PlatformError('conflict', `quality event ${event.type} is invalid from ${state.phase}`);
  }
  const next = target(event, state);
  if (next.durableDeadline !== null && (!Number.isSafeInteger(next.durableDeadline) || next.durableDeadline <= 0)) {
    throw new PlatformError('validation', 'quality transition deadline must be a positive integer timestamp');
  }
  return validateQualityRunStateV2(next);
}

export function replayQualityTransition(
  existing: QualityTransitionRecordV1 | null,
  event: QualityTransitionEventV1,
): 'new' | 'identical' {
  if (!existing) return 'new';
  if (existing.eventId !== event.id || existing.eventDigest !== qualityTransitionEventDigest(event)) {
    throw new PlatformError('conflict', 'quality transition event id was reused with a different payload');
  }
  return 'identical';
}
